import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from 'react'
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signOut as firebaseSignOut,
  resetPassword as firebaseResetPassword,
  subscribeToAuthChanges,
} from '../services/firebase/auth'
import { useTranslation } from './LanguageContext'
import type { User } from '../types/user'

interface AuthContextValue {
  user: User | null
  loading: boolean
  error: string | null
  isAdmin: boolean
  signUp: (email: string, password: string, displayName?: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { t } = useTranslation()
  const tRef = useRef(t)
  useEffect(() => { tRef.current = t }, [t])

  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((authUser) => {
      setUser(authUser)
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const handleAuthError = useCallback((err: unknown): string => {
    const tr = tRef.current
    if (err instanceof Error) {
      const firebaseError = err as { code?: string }
      switch (firebaseError.code) {
        case 'auth/email-already-in-use':
          return tr.authErrors.emailAlreadyInUse
        case 'auth/invalid-email':
          return tr.authErrors.invalidEmail
        case 'auth/operation-not-allowed':
          return tr.authErrors.operationNotAllowed
        case 'auth/weak-password':
          return tr.authErrors.weakPassword
        case 'auth/user-disabled':
          return tr.authErrors.userDisabled
        case 'auth/user-not-found':
          return tr.authErrors.userNotFound
        case 'auth/wrong-password':
          return tr.authErrors.wrongPassword
        case 'auth/invalid-credential':
          return tr.authErrors.invalidCredential
        case 'auth/too-many-requests':
          return tr.authErrors.tooManyRequests
        case 'auth/popup-closed-by-user':
          return tr.authErrors.popupClosed
        default:
          return err.message || tr.authErrors.authError
      }
    }
    return tr.authErrors.unknownError
  }, [])

  const signUp = useCallback(
    async (email: string, password: string, displayName?: string) => {
      setError(null)
      setLoading(true)
      try {
        await signUpWithEmail(email, password, displayName)
      } catch (err) {
        const errorMessage = handleAuthError(err)
        setError(errorMessage)
        throw new Error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null)
    setLoading(true)
    try {
      await signInWithEmail(email, password)
    } catch (err) {
      const errorMessage = handleAuthError(err)
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleGoogleSignIn = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      await signInWithGoogle()
    } catch (err) {
      const errorMessage = handleAuthError(err)
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    setError(null)
    try {
      await firebaseSignOut()
    } catch (err) {
      const errorMessage = handleAuthError(err)
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    setError(null)
    try {
      await firebaseResetPassword(email)
    } catch (err) {
      const errorMessage = handleAuthError(err)
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const isAdmin = user?.role === 'admin'

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAdmin,
    signUp,
    signIn,
    signInWithGoogle: handleGoogleSignIn,
    signOut,
    resetPassword,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
